<template>
    <div>
        <CheckSession v-if="!session_verified && !is_user_logged_in" :is_pre2fa_route="is_pre2fa_route"></CheckSession>
        <template v-if="check_session_executed">
            <loader v-if="whitelabel.length === 0"></loader>
            <template v-else>
                <GuestLayout v-if="is_guest">
                    <router-view></router-view>
                </GuestLayout>
                <MainLayout v-else>
                    <div class="container-fluid">
                        <div class="row">
                            <SystemMessages v-if="show_system_messages === true"></SystemMessages>
                            <div class="container-ui col-xs-12 col-md-8 col-lg-9 col-xl-8">
                                <SystemAlerts v-if="show_system_messages === true"></SystemAlerts>
                                <template v-if="show_system_messages === true">
                                    <router-view></router-view>
                                </template>
                                <template v-else>
                                    <router-view></router-view>
                                </template>
                            </div>
                        </div>
                    </div>
                </MainLayout>
            </template>
        </template>
        <vue-progress-bar></vue-progress-bar>
        <modals-container></modals-container>
        <v-dialog></v-dialog>
        <vue-notifications position="top right" :duration="5000" classes="ccs-notification"></vue-notifications>
        <ccs-modal></ccs-modal>
    </div>
</template>

<script>
import { mapState } from "vuex";

import GuestLayout from "@/components/GuestLayout.vue";
import MainLayout from "@/components/MainLayout.vue";
import SystemMessages from "@/components/SystemMessages.vue";
import SystemAlerts from "@/components/SystemAlerts.vue";
import CheckSession from "@/components/CheckSession.vue";

export default {
    metaInfo() {
        return {
            title: "Dashboard",
            // all titles will be injected into this template
            titleTemplate: `%s | ${this.whitelabel.NAME}`
        };
    },
    components: {
        GuestLayout,
        MainLayout,
        CheckSession,
        SystemMessages,
        SystemAlerts
    },
    data() {
        return {
            access_granted: false,

            blacklistInterceptorUrls: [
                this.$laroute.route("domains.ui_ajax_get_data"),
                this.$laroute.route("get_platform_updates")
            ],

            routes_to_hide_system_messages: [
                "user.logout",
                "login.main",
                "login.force_2fa",
                "forgot_password.main",
                "reset_password.main",
                "invite_account.main",
                "user.login_as_support"
            ],

            routes_to_avoid_check_session: [
                "user.logout",
                "login.main",
                "forgot_password.main",
                "reset_password.main",
                "invite_account.main",
                "user.login_as_support"
            ],

            routes_pre2fa: ["login.force_2fa"]
        };
    },
    computed: {
        ...mapState({
            whitelabel: "whitelabel"
        }),
        show_system_messages: function() {
            if (this.$route === undefined) {
                return false;
            }

            if (this.routes_to_hide_system_messages.indexOf(this.$route.name) === -1) {
                return true;
            }

            return false;
        },
        run_check_session: function() {
            if (this.$route === undefined) {
                return false;
            }

            if (this.routes_to_avoid_check_session.indexOf(this.$route.name) === -1) {
                return true;
            }

            return false;
        },
        is_pre2fa_route: function() {
            if (this.$route === undefined) {
                return false;
            }

            if (this.routes_pre2fa.indexOf(this.$route.name) === -1) {
                return false;
            }

            return true;
        },
        is_user_logged_in: function() {
            return this.$store.state.user.user_logged_in;
        },
        session_verified: function() {
            return this.$store.state.user.session_verified;
        },
        check_session_executed: function() {
            return this.$store.state.user.check_session_executed;
        },
        is_guest: function() {
            return (
                this.is_pre2fa_route ||
                this.is_user_logged_in === false ||
                this.routes_to_avoid_check_session.indexOf(this.$route.name) !== -1
            );
        }
    },
    mounted() {
        //  [App.vue specific] When App.vue is finish loading finish the progress bar
        this.$Progress.finish();
        this.getSystemNotifications();
    },
    created() {
        //  [App.vue specific] When App.vue is first loaded start the progress bar
        this.$Progress.start();
        this.$store.dispatch("getWhitelabelSettings");
        //  hook the progress bar to start before we move router-view
        this.$router.beforeEach((to, from, next) => {
            //  does the page we want to go to have a meta.progress object
            if (to.meta.progress !== undefined) {
                let meta = to.meta.progress;
                // parse meta tags
                this.$Progress.parseMeta(meta);
            }
            //  start the progress bar
            this.$Progress.start();
            //  continue to next page
            next();
        });
        //  hook the progress bar to finish after we've finished moving router-view
        this.$router.afterEach((to, from) => {
            //  finish the progress bar
            this.$Progress.finish();
        });
    },
    beforeCreate() {
        this.$axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";
        // commiting directly the meta right now and not using the action
        this.$store.commit("csrf", document.querySelector("meta[name='csrf-token']").getAttribute("content"));

        this.$axios.interceptors.request.use(async (config) => {
            // always skip the csrf url
            // disabled for now since we are using meta csrf
            /*
            if (config.url != "/manager/csrf" && !this.$store.state.csrf) {
                // might have a few in parallel, but we don't care right now since the csrf is once per login
                // commiting directly the meta right now and not using the action
                await this.$store.dispatch("csrf");
            }
            */

            config.headers.common["X-CSRF-TOKEN"] = this.$store.state.csrf;

            if (this.blacklistInterceptorUrls.includes(config.url) === false) {
                this.$Progress.start(); // for every request that's not in the blacklist start the automatic progress bar
            }
            return config;
        });

        this.$axios.interceptors.response.use(
            (response) => {
                this.$Progress.finish();
                // Do something with response data
                if (response.data.status_code == 401) {
                    if (this.$store.state.routes_to_avoid_check_session.indexOf(this.$route.name) !== -1) {
                    } else {
                        window.location.href = this.loginPageUrl(window.location.href);
                    }
                }
                // if setting up account StatusCode::EXPECTATION_FAILED or expired login StatusCode::FORBIDDEN or engine timeout
                if (
                    response.data.status_code == 417 ||
                    response.data.status_code == 403 ||
                    response.data.status_code == 504
                ) {
                    //console.log('AAAAAAA2222');
                    window.location.href = "/manager";
                }

                return response;
            },
            (error) => {
                this.$Progress.fail();

                if (error && error.hasOwnProperty("response")) {
                    // we need to handle the response error codes here as well
                    if (error.response.data.status_code == 401) {
                        //console.log('AAAAAAA333', window.location.href);
                        window.location.href = this.loginPageUrl(window.location.href);
                        return Promise.reject(error);
                    }
                    // if setting up account StatusCode::EXPECTATION_FAILED or expired login StatusCode::FORBIDDEN or engine timeout
                    if (
                        error.response.data.status_code == 417 ||
                        error.response.data.status_code == 403 ||
                        error.response.data.status_code == 504
                    ) {
                        //console.log('AAAAAAA444444');
                        window.location.href = "/manager";
                        return Promise.reject(error);
                    }
                }

                // Do something with response error
                let text = "No additional information at this moment. Please contact our support team.";
                let title = "Request could not be made...";

                if (error.response && error.response.data) {
                    if (error.response.data.message && error.response.data.message.trim() !== "") {
                        text = error.response.data.message;
                    }

                    if (error.response.data.title && error.response.data.title.trim() !== "") {
                        title = error.response.data.title;
                    }

                    if (error.response.data.page_title && error.response.data.page_title.trim() !== "") {
                        title = error.response.data.page_title;
                    }
                } else if (error.response) {
                    title = JSON.stringify(error.response);
                } else if (error) {
                    title = JSON.stringify(error);
                }

                this.$modal.show("ccs-modal-dialog", {
                    type: "error",
                    title: `${title}`,
                    text: `${text}`
                });

                return Promise.reject(error);
            }
        );
    },
    methods: {
        async getSystemNotifications() {
            if (this.is_user_logged_in === false) {
                setTimeout(this.getSystemNotifications, 1000);
                return;
            }

            try {
                await this.$store.dispatch("platformUpdates/getNotifications");
            } catch (e) {
                // TODO handle errors
            } finally {
                // if(process.env.PRODUCTION === true)
                if (1) {
                    setTimeout(this.getSystemNotifications, 1000);
                }
            }
        },
        loginPageUrl(origin) {
            if (origin.indexOf("dewaweb.cloud") > 0) {
                return "https://cluster.dewaweb.cloud/ui/login";
            } else if (origin.indexOf("clustercs.com") > 0) {
                return "https://ccs.clustercs.com/ui/login";
            } else if (origin.indexOf("ccsdev1.webland.ro") > 0) {
                return "https://ccsdev1.webland.ro/ui/login";
            } else if (origin.indexOf("ccsdev3.webland.ro") > 0) {
                return "https://ccsdev3.webland.ro/ui/login";
            } else if (origin.indexOf("ccsdev5.webland.ro") > 0) {
                return "https://ccsdev5.webland.ro/ui/login";
            } else if (origin.indexOf("flexiant5.webland.ro") > 0) {
                return "https://flexiant5.webland.ro/portal/clientarea";
            }
            return "/ui/login";
        },
        logout: function() {
            this.$modal.show("ccs-modal-dialog", {
                type: "warning",
                title: "Log out",
                text: "Are you sure you want to log out?",
                buttons: [
                    {
                        type: "success",
                        title: "Ok",
                        class: "c-btn c-btn--success c-btn--primary",
                        handler: () => {
                            this.$modal.hide("ccs-modal-dialog");
                            this.$modal.show("ccs-modal-dialog", {
                                type: "info",
                                title: "Logging out",
                                text: "Please wait ...",
                                buttons: [
                                    {
                                        title: "Close",
                                        default: true // Will be triggered by default if 'Enter' pressed.
                                    }
                                ]
                            });
                            this.$store.dispatch("user/LogoutUser").then(
                                (success_data) => {
                                    this.$modal.hide("ccs-modal-dialog");
                                    if (success_data.user_logged_in && "company_id" in success_data.user_data) {
                                        this.$router.push({
                                            name: "login.main",
                                            params: {
                                                company_id: success_data.user_data.company_id
                                            }
                                        });
                                    } else {
                                        this.$router.push({
                                            name: "login.main"
                                        });
                                    }
                                },
                                (error_data) => {
                                    this.$modal.hide("ccs-modal-dialog");
                                    this.$modal.show("ccs-modal-dialog", {
                                        type: "error",
                                        title: "Error while attempting to logout",
                                        text: error_data,
                                        buttons: [
                                            {
                                                title: "Close",
                                                default: true // Will be triggered by default if 'Enter' pressed.
                                            }
                                        ]
                                    });
                                    return;
                                }
                            );
                        }
                    },
                    {
                        title: "Cancel",
                        default: true // Will be triggered by default if 'Enter' pressed.
                    }
                ]
            });
        },
        logoutAsUser: function() {
            this.$modal.show("ccs-modal-dialog", {
                type: "warning",
                title: "Log out as user",
                text: "Are you sure you want to log out as current user?",
                buttons: [
                    {
                        type: "success",
                        title: "Ok",
                        class: "c-btn c-btn--success c-btn--primary",
                        handler: () => {
                            this.$modal.hide("ccs-modal-dialog");
                            this.$modal.show("ccs-modal-dialog", {
                                type: "info",
                                title: "Logging out",
                                text: "Please wait ...",
                                buttons: [
                                    {
                                        title: "Close",
                                        default: true // Will be triggered by default if 'Enter' pressed.
                                    }
                                ]
                            });
                            this.$store.dispatch("user/LogoutAsUser").then(
                                (success_data) => {
                                    this.$modal.hide("ccs-modal-dialog");
                                    window.location.href = this.$router.resolve({ name: "account.information" }).href;
                                },
                                (error_data) => {
                                    this.$modal.hide("ccs-modal-dialog");
                                    this.$modal.show("ccs-modal-dialog", {
                                        type: "error",
                                        title: "Error while attempting to logout as user",
                                        text: error_data,
                                        buttons: [
                                            {
                                                title: "Close",
                                                default: true // Will be triggered by default if 'Enter' pressed.
                                            }
                                        ]
                                    });
                                    return;
                                }
                            );
                        }
                    },
                    {
                        title: "Cancel",
                        default: true // Will be triggered by default if 'Enter' pressed.
                    }
                ]
            });
        }
    }
};
</script>

<style lang="scss" src="./app.scss"></style>
